import { useState, useRef, useEffect } from "react";
import {
  CaretDown,
  CaretRight,
  CaretUp,
  DotsThree,
  Plus,
  PencilSimple,
  Palette,
  ArrowUp,
  ArrowDown,
  Trash,
} from "@phosphor-icons/react";
import TaskRow from "./TaskRow";
import "../NivTasksPage.css";

/**
 * One collapsible colored group (Monday-style) with a task table + add-task row.
 *
 * @param {Object}   list           - { id, name, color, collapsed }
 * @param {Array}    tasks          - visible tasks for this list (sorted)
 * @param {number}   hiddenDoneCount
 * @param {Object}   config         - board config
 * @param {Object}   listActions    - { onToggleCollapse, onRename, onRecolor, onMoveUp, onMoveDown, onDelete, canMoveUp, canMoveDown }
 * @param {Object}   taskActions    - { onUpdate, onDelete, onOpenModal, onAdd }
 * @param {Object}   sort           - { field, dir } | null (column sort; disables drag)
 * @param {function} onSortChange   - (field) => void
 * @param {Object}   dnd            - { enabled, draggedTaskId, dropIndex, onDragStart, onDragEnd, onDragOver, onDrop }
 */
function TaskListGroup({
  list,
  tasks,
  hiddenDoneCount,
  config,
  listActions,
  taskActions,
  sort,
  onSortChange,
  dnd,
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const [nameDraft, setNameDraft] = useState(list.name);
  const [newTaskTitle, setNewTaskTitle] = useState("");
  const nameInputRef = useRef(null);
  const colorInputRef = useRef(null);

  useEffect(() => {
    if (isRenaming) nameInputRef.current?.select();
  }, [isRenaming]);

  const startRename = () => {
    setNameDraft(list.name);
    setIsRenaming(true);
    setMenuOpen(false);
  };

  const commitRename = () => {
    setIsRenaming(false);
    const trimmed = nameDraft.trim();
    if (trimmed && trimmed !== list.name) {
      listActions.onRename(list.id, trimmed);
    }
  };

  const handleAddTask = (e) => {
    if (e.key !== "Enter") return;
    const trimmed = newTaskTitle.trim();
    if (!trimmed) return;
    taskActions.onAdd(list.id, trimmed);
    setNewTaskTitle("");
  };

  const isCollapsed = list.collapsed === true;

  const SortHeader = ({ field, label, extraClass = "" }) => {
    const active = sort?.field === field;
    return (
      <button
        type="button"
        className={`nivtasks-cell nivtasks-sort-header ${extraClass} ${active ? "is-sorted" : ""}`}
        onClick={() => onSortChange(field)}
        title="Sort by this column"
      >
        <span>{label}</span>
        {active &&
          (sort.dir === "asc" ? (
            <CaretUp size={12} weight="bold" />
          ) : (
            <CaretDown size={12} weight="bold" />
          ))}
      </button>
    );
  };

  return (
    <section className="nivtasks-group" style={{ "--group-color": list.color }}>
      <header className="nivtasks-group-header">
        <button
          type="button"
          className="nivtasks-group-caret"
          onClick={() => listActions.onToggleCollapse(list.id)}
          aria-label={isCollapsed ? "Expand group" : "Collapse group"}
        >
          {isCollapsed ? <CaretRight size={16} weight="bold" /> : <CaretDown size={16} weight="bold" />}
        </button>

        {isRenaming ? (
          <input
            ref={nameInputRef}
            className="nivtasks-group-name-input"
            value={nameDraft}
            onChange={(e) => setNameDraft(e.target.value)}
            onBlur={commitRename}
            onKeyDown={(e) => {
              if (e.key === "Enter") commitRename();
              if (e.key === "Escape") setIsRenaming(false);
            }}
          />
        ) : (
          <button type="button" className="nivtasks-group-name" onClick={startRename} title="Click to rename">
            {list.name}
          </button>
        )}

        <span className="nivtasks-group-count">
          {tasks.length} {tasks.length === 1 ? "task" : "tasks"}
        </span>
        {hiddenDoneCount > 0 && (
          <span className="nivtasks-group-done-badge">{hiddenDoneCount} done hidden</span>
        )}

        <div className="nivtasks-group-menu-wrap">
          <button
            type="button"
            className="nivtasks-icon-btn"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Group options"
          >
            <DotsThree size={20} weight="bold" />
          </button>
          {menuOpen && (
            <>
              <div className="nivtasks-popover-backdrop" onClick={() => setMenuOpen(false)} />
              <div className="nivtasks-menu">
                <button type="button" onClick={startRename}>
                  <PencilSimple size={15} /> Rename
                </button>
                <button
                  type="button"
                  onClick={() => {
                    colorInputRef.current?.click();
                  }}
                >
                  <Palette size={15} /> Change color
                </button>
                <button
                  type="button"
                  disabled={!listActions.canMoveUp}
                  onClick={() => {
                    listActions.onMoveUp(list.id);
                    setMenuOpen(false);
                  }}
                >
                  <ArrowUp size={15} /> Move up
                </button>
                <button
                  type="button"
                  disabled={!listActions.canMoveDown}
                  onClick={() => {
                    listActions.onMoveDown(list.id);
                    setMenuOpen(false);
                  }}
                >
                  <ArrowDown size={15} /> Move down
                </button>
                <button
                  type="button"
                  className="nivtasks-menu-danger"
                  onClick={() => {
                    listActions.onDelete(list);
                    setMenuOpen(false);
                  }}
                >
                  <Trash size={15} /> Delete list
                </button>
              </div>
            </>
          )}
          <input
            ref={colorInputRef}
            type="color"
            className="nivtasks-color-input-hidden"
            value={list.color}
            onChange={(e) => {
              listActions.onRecolor(list.id, e.target.value);
              setMenuOpen(false);
            }}
          />
        </div>
      </header>

      {!isCollapsed && (
        <div className="nivtasks-table-scroll">
          <div
            className={`nivtasks-table ${dnd?.enabled ? "is-dnd" : ""}`}
            onDragOver={(e) => {
              // Allow dropping into an empty area / below the last row
              if (!dnd?.enabled || !dnd.draggedTaskId) return;
              e.preventDefault();
              dnd.onDragOver(list.id, tasks.length);
            }}
            onDrop={(e) => {
              if (!dnd?.enabled || !dnd.draggedTaskId) return;
              e.preventDefault();
              dnd.onDrop();
            }}
          >
            <div className="nivtasks-row nivtasks-row-head">
              <div className="nivtasks-cell nivtasks-cell-drag" />
              <SortHeader field="title" label="Task" extraClass="nivtasks-cell-title" />
              <SortHeader field="status" label="Status" />
              <SortHeader field="priority" label="Priority" />
              <SortHeader field="type" label="Type" />
              <div className="nivtasks-cell nivtasks-cell-actions" />
            </div>

            {tasks.map((task, index) => (
              <TaskRow
                key={task.id}
                task={task}
                index={index}
                config={config}
                onUpdate={taskActions.onUpdate}
                onDelete={taskActions.onDelete}
                onOpenModal={taskActions.onOpenModal}
                dnd={dnd}
                listId={list.id}
              />
            ))}

            {/* Drop line at the very end of the list */}
            {dnd?.enabled && dnd.dropIndex === tasks.length && (
              <div className="nivtasks-drop-line" />
            )}

            {tasks.length === 0 && hiddenDoneCount === 0 && (
              <div className="nivtasks-empty-row">No tasks yet</div>
            )}

            <div className="nivtasks-addtask-row">
              <Plus size={15} className="nivtasks-addtask-icon" />
              <input
                className="nivtasks-addtask-input"
                placeholder="Add task…"
                value={newTaskTitle}
                onChange={(e) => setNewTaskTitle(e.target.value)}
                onKeyDown={handleAddTask}
              />
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default TaskListGroup;
